import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Enemy implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  
  // 3D position in tunnel
  private angle: number;        // Angle around tunnel (0-2π)
  private distance: number;     // Distance from tunnel center
  private depth: number;        // Depth in tunnel (0 = at player, 1 = far end)
  
  // Enemy properties
  private baseSize: number = 20;
  private animationTimer: number = 0;
  private rotationSpeed: number;
  private enemyType: 'flipper' | 'tanker' | 'spiker' | 'spike' | 'fuseball' | 'pulsar';
  private moveSpeed: number = 1.0; // Base movement speed multiplier

  constructor(
    id: string,
    angle: number,
    distance: number,
    depth: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.angle = angle;
    this.distance = distance;
    this.depth = depth;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    
    // Set enemy type based on random chance (following Tempest enemy distribution)
    const rand = Math.random();
    if (rand < 0.35) {
      this.enemyType = 'flipper';
      this.rotationSpeed = 3;
      this.moveSpeed = 1.2;
      this.baseSize = 18;
    } else if (rand < 0.65) {
      this.enemyType = 'tanker';
      this.rotationSpeed = 1;
      this.moveSpeed = 0.8;
      this.baseSize = 24;
    } else if (rand < 0.85) {
      this.enemyType = 'spiker';
      this.rotationSpeed = 2;
      this.moveSpeed = 1.5;
      this.baseSize = 16;
    } else if (rand < 0.92) {
      this.enemyType = 'spike';
      this.rotationSpeed = 0;
      this.moveSpeed = 2.0;
      this.baseSize = 12;
    } else if (rand < 0.98) {
      this.enemyType = 'fuseball';
      this.rotationSpeed = 6;
      this.moveSpeed = 0.6;
      this.baseSize = 20;
    } else {
      this.enemyType = 'pulsar';
      this.rotationSpeed = 4;
      this.moveSpeed = 1.0;
      this.baseSize = 22;
    }
    
    // Calculate initial 2D position for interface compatibility
    this.position = this.calculateScreenPosition(800, 600);
    this.size = { x: this.baseSize, y: this.baseSize };
  }

  public update(deltaTime: number): void {
    // Default tunnel speed - can be overridden by game
    this.updateWithTunnelSpeed(deltaTime, 2);
  }

  public updateWithTunnelSpeed(deltaTime: number, tunnelSpeed: number): void {
    // Move enemy closer (reduce depth) based on enemy-specific speed
    this.depth -= tunnelSpeed * deltaTime * 0.1 * this.moveSpeed;
    
    // Update animation
    this.animationTimer += deltaTime;
    
    // Enemy-specific movement patterns
    switch (this.enemyType) {
      case 'flipper':
        // Flippers move straight down the tube but flip/rotate
        this.angle += this.rotationSpeed * deltaTime * 0.5;
        break;
      case 'tanker':
        // Tankers move slowly but steadily
        break;
      case 'spiker':
        // Spikers zig-zag as they approach
        this.angle += Math.sin(this.animationTimer * 4) * deltaTime * 0.8;
        break;
      case 'spike':
        // Spikes move fast and straight
        break;
      case 'fuseball':
        // Fuseballs spin wildly and move erratically
        this.angle += this.rotationSpeed * deltaTime * 0.8;
        this.distance += Math.sin(this.animationTimer * 6) * deltaTime * 10;
        break;
      case 'pulsar':
        // Pulsars pulsate and move with rhythm
        this.baseSize = 22 + Math.sin(this.animationTimer * 8) * 6;
        break;
    }
    
    // Update 2D position based on 3D coordinates
    this.position = this.calculateScreenPosition(800, 600);
    
    // Mark as inactive if passed the player
    if (this.depth < -0.1) {
      this.active = false;
    }
  }

  public getDepth(): number {
    return this.depth;
  }
  
  public getEnemyType(): string {
    return this.enemyType;
  }

  public getScreenPosition(screenWidth: number, screenHeight: number): Vector2 {
    return this.calculateScreenPosition(screenWidth, screenHeight);
  }

  private calculateScreenPosition(screenWidth: number, screenHeight: number): Vector2 {
    // Convert 3D tunnel coordinates to 2D screen coordinates
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;
    
    // Apply perspective scaling based on depth
    const scale = Math.max(0.1, 1 - this.depth * 0.8);
    const scaledDistance = this.distance * scale;
    
    const x = centerX + Math.cos(this.angle) * scaledDistance;
    const y = centerY + Math.sin(this.angle) * scaledDistance;
    
    return { x, y };
  }

  public getSize(): number {
    // Size scales with depth for 3D effect
    const scale = Math.max(0.1, 1 - this.depth * 0.8);
    return this.baseSize * scale;
  }

  public destroy(): void {
    this.active = false;
  }

  public getBounds(): Rectangle {
    const size = this.getSize();
    return {
      x: this.position.x - size / 2,
      y: this.position.y - size / 2,
      width: size,
      height: size
    };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Default screen dimensions - can be overridden
    this.renderWithDimensions(ctx, 800, 600);
  }

  public renderWithDimensions(ctx: CanvasRenderingContext2D, screenWidth: number, screenHeight: number): void {
    if (!this.active || this.depth > 1 || this.depth < -0.1) return;
    
    const pos = this.getScreenPosition(screenWidth, screenHeight);
    const size = this.getSize();
    const centerX = pos.x;
    const centerY = pos.y;
    
    // Don't render if too small or off screen
    if (size < 2 || centerX < -50 || centerX > screenWidth + 50 || 
        centerY < -50 || centerY > screenHeight + 50) {
      return;
    }
    
    // Render based on enemy type
    switch (this.enemyType) {
      case 'flipper':
        this.renderFlipper(centerX, centerY, size);
        break;
      case 'tanker':
        this.renderTanker(centerX, centerY, size);
        break;
      case 'spiker':
        this.renderSpiker(centerX, centerY, size);
        break;
      case 'spike':
        this.renderSpike(centerX, centerY, size);
        break;
      case 'fuseball':
        this.renderFuseball(centerX, centerY, size);
        break;
      case 'pulsar':
        this.renderPulsar(centerX, centerY, size);
        break;
    }
    
    // Add depth-based transparency
    const alpha = Math.max(0.3, 1 - this.depth * 0.7);
    if (alpha < 1) {
      // Apply alpha by drawing with semi-transparent overlay
      const overlayAlpha = 1 - alpha;
      this.renderManager.drawEllipse(
        centerX - size/2, centerY - size/2, size/2, size/2,
        `rgba(0, 0, 0, ${overlayAlpha * 0.5})`, true
      );
    }
  }

  private renderFlipper(centerX: number, centerY: number, size: number): void {
    const radius = size / 2;
    const rotation = this.animationTimer * this.rotationSpeed;
    
    // Flipper body (U-shaped like original Tempest)
    const flipperColor = '#FFFF00'; // Yellow
    
    // Draw U-shape with rotation
    const points = [];
    const segments = 8;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI + rotation;
      const x = centerX + Math.cos(angle) * radius * 0.8;
      const y = centerY + Math.sin(angle) * radius * 0.8;
      points.push({ x, y });
    }
    
    this.renderManager.drawPolygon(points, flipperColor, false, 2);
    
    // Center dot
    this.renderManager.drawEllipse(
      centerX - 2, centerY - 2, 2, 2,
      flipperColor, true
    );
  }

  private renderTanker(centerX: number, centerY: number, size: number): void {
    const halfSize = size / 2;
    const tankerColor = '#FF6600'; // Orange
    
    // Tanker body (rectangular with rounded corners)
    const points = [
      { x: centerX - halfSize * 0.8, y: centerY - halfSize * 0.6 },
      { x: centerX + halfSize * 0.8, y: centerY - halfSize * 0.6 },
      { x: centerX + halfSize * 0.8, y: centerY + halfSize * 0.6 },
      { x: centerX - halfSize * 0.8, y: centerY + halfSize * 0.6 }
    ];
    
    this.renderManager.drawPolygon(points, tankerColor, true);
    this.renderManager.drawPolygon(points, '#FFFFFF', false, 2);
    
    // Tank treads
    this.renderManager.drawLine(
      centerX - halfSize * 0.6, centerY + halfSize * 0.3,
      centerX + halfSize * 0.6, centerY + halfSize * 0.3,
      '#CCCCCC', 2
    );
  }

  private renderSpiker(centerX: number, centerY: number, size: number): void {
    const radius = size / 2;
    const spikerColor = '#FF0080'; // Pink/Magenta
    
    // Spiker body (star shape)
    const spikes = 6;
    const rotation = this.animationTimer * this.rotationSpeed;
    
    for (let i = 0; i < spikes; i++) {
      const angle = (i / spikes) * Math.PI * 2 + rotation;
      const innerAngle = ((i + 0.5) / spikes) * Math.PI * 2 + rotation;
      
      const outerX = centerX + Math.cos(angle) * radius;
      const outerY = centerY + Math.sin(angle) * radius;
      const innerX = centerX + Math.cos(innerAngle) * radius * 0.4;
      const innerY = centerY + Math.sin(innerAngle) * radius * 0.4;
      
      this.renderManager.drawPolygon([
        { x: centerX, y: centerY },
        { x: outerX, y: outerY },
        { x: innerX, y: innerY }
      ], spikerColor, true);
    }
    
    // Center
    this.renderManager.drawEllipse(
      centerX - 3, centerY - 3, 3, 3,
      '#FFFFFF', true
    );
  }
  
  private renderSpike(centerX: number, centerY: number, size: number): void {
    const halfSize = size / 2;
    const spikeColor = '#FFFFFF'; // White
    
    // Simple diamond/spike shape
    const points = [
      { x: centerX, y: centerY - halfSize },
      { x: centerX + halfSize * 0.6, y: centerY },
      { x: centerX, y: centerY + halfSize },
      { x: centerX - halfSize * 0.6, y: centerY }
    ];
    
    this.renderManager.drawPolygon(points, spikeColor, true);
    this.renderManager.drawPolygon(points, '#CCCCCC', false, 1);
  }
  
  private renderFuseball(centerX: number, centerY: number, size: number): void {
    const radius = size / 2;
    const fuseballColor = '#FF4500'; // Red-Orange
    
    // Main body
    this.renderManager.drawEllipse(
      centerX - radius, centerY - radius, radius, radius,
      fuseballColor, true
    );
    
    // Chaotic energy spikes
    const spikes = 12;
    for (let i = 0; i < spikes; i++) {
      const angle = (i / spikes) * Math.PI * 2 + this.animationTimer * this.rotationSpeed;
      const length = radius * (0.5 + Math.sin(this.animationTimer * 8 + i) * 0.4);
      const endX = centerX + Math.cos(angle) * (radius + length);
      const endY = centerY + Math.sin(angle) * (radius + length);
      
      this.renderManager.drawLine(centerX, centerY, endX, endY, '#FFFF00', 1);
    }
    
    // Pulsing core
    const coreSize = 2 + Math.sin(this.animationTimer * 10) * 2;
    this.renderManager.drawEllipse(
      centerX - coreSize, centerY - coreSize, coreSize, coreSize,
      '#FFFFFF', true
    );
  }
  
  private renderPulsar(centerX: number, centerY: number, size: number): void {
    const radius = size / 2;
    const pulsarColor = '#00FFFF'; // Cyan
    const pulseIntensity = Math.sin(this.animationTimer * 8);
    
    // Pulsating rings
    for (let ring = 0; ring < 3; ring++) {
      const ringRadius = radius * (0.3 + ring * 0.3) * (1 + pulseIntensity * 0.3);
      const alpha = 0.8 - ring * 0.2 + pulseIntensity * 0.2;
      
      this.renderManager.drawEllipse(
        centerX - ringRadius, centerY - ringRadius, ringRadius, ringRadius,
        `rgba(0, 255, 255, ${Math.max(0, alpha)})`, false, 2
      );
    }
    
    // Center core
    this.renderManager.drawEllipse(
      centerX - 4, centerY - 4, 4, 4,
      pulsarColor, true
    );
  }
}